import React, { useEffect, useState } from "react";
import { useTranslation } from "../../../utils/useTranslation";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { ThemeColors } from "../../../utils/constants/colors";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Dimensions,
} from "react-native";
import { AntDesign, Feather } from "@expo/vector-icons";
import * as Sharing from "expo-sharing";
import {
  validSignedURL,
  validSignedURLWithModule,
} from "../../../utils/validateSignedUrl";

const CustomerOrderSummaryReport = () => {
  const route = useRoute();
  const navigation: any = useNavigation();
  const { order } = route.params as { order: any };
  const { t } = useTranslation();
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);

  const [windowWidth, setWindowWidth] = useState(
    Dimensions.get("window").width,
  );
  const isMobile = windowWidth < 768;

  const [dnAttachments, setDnAttachments] = useState<{
    [key: string]: string | null;
  }>({});

  useEffect(() => {
    const sub = Dimensions.addEventListener("change", ({ window }) =>
      setWindowWidth(window.width),
    );
    return () => sub?.remove();
  }, []);

  // Fetch DN attachments
  useEffect(() => {
    if (order?.details?.length) {
      const newAttachments: { [key: string]: string | null } = {};
      order.details.forEach((item: any) => {
        if (item.documentNumber) {
          validSignedURLWithModule(
            "DN" + item.documentNumber,
            "label",
            "delivery-note",
            () => {},
            (url: string | null) => {
              newAttachments[item._id] = url || null;
              setDnAttachments((prev) => ({ ...prev, ...newAttachments }));
            },
            "pdf/*",
          );
        } else {
          newAttachments[item._id] = null;
        }
      });
    }
  }, [order?.details]);

  const exportToCSV = () => {
    if (!order?.details?.length) return;

    const headers = [
      "order_number",
      "order_date",
      "item_name",
      "supplier",
      "units_qty_per_box",
      "units_qty_per_pallet",
      "pallets_ordered_qty",
      "total_units_ordered",
    ];

    const rows = order.details.map((item: any) => [
      order.orderNo,
      new Date(order.createdAt).toLocaleDateString("en-AU"),
      item.itemName,
      "Multisteps",
      item.perCtns,
      item.perPallet,
      item.uom === "Pallets"
        ? item.quantity
        : item.uom === "Pieces"
          ? item.quantity / item.perPallets
          : item.quantity / item.perCtns,
      item.uom === "Pieces"
        ? item.quantity
        : item.uom === "Pallets"
          ? item.quantity * item.perPallet
          : item.quantity * item.perCtns,
    ]);

    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `order_${order.orderNo}_items.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    if (!order?.details?.length) return;

    const jsonData = order.details.map((item: any) => ({
      order_number: order.orderNo,
      order_date: new Date(order.createdAt).toLocaleDateString("en-AU"),
      item_name: item.itemName,
      supplier: "Multisteps",
      units_qty_per_box: item.perCtns,
      units_qty_per_pallet: item.perPallet,
      pallets_ordered_qty:
        item.uom === "Pallets"
          ? item.quantity
          : item.uom === "Pieces"
            ? item.quantity / item.perPallets
            : item.quantity / item.perCtns,
      total_units_ordered:
        item.uom === "Pieces"
          ? item.quantity
          : item.uom === "Pallets"
            ? item.quantity * item.perPallet
            : item.quantity * item.perCtns,
    }));

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `order_${order.orderNo}_items.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!order) return null;

  return (
    <ScrollView style={styles.page}>
      {/* HEADER */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation?.navigate("CustomerOrderReport")}
        >
          <AntDesign name="arrowleft" size={24} color={colors.text} />
        </TouchableOpacity>

        <View style={styles.headerRight}>
          <Text style={styles.orderNo}>{order.orderNo}</Text>
          <View style={styles.statusChip}>
            <Text style={styles.statusText}>{order.orderStatus}</Text>
          </View>
        </View>
      </View>

      {/* ORDER META */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{t("orderInformation")}</Text>
        <MetaRow
          label={t("organization")}
          value={order.organization?.name}
          styles={styles}
        />
        <MetaRow
          label={t("subOrganization")}
          value={order.subOrganization?.name}
          styles={styles}
        />
        <MetaRow
          label={t("requestType")}
          value={order.requestType}
          styles={styles}
        />
        <MetaRow
          label={t("createdBy")}
          value={order.createdBy?.auth?.email}
          styles={styles}
        />
        <MetaRow
          label={t("createdAt")}
          value={new Date(order.createdAt).toLocaleString()}
          styles={styles}
        />
      </View>

      {/* EXPORT BUTTONS */}
      {order?.details?.length > 0 && (
        <View style={styles.exportRow}>
          <TouchableOpacity style={styles.exportBtnCSV} onPress={exportToCSV}>
            <Feather name="download" size={13} color="#fff" />
            <Text style={styles.exportBtnText}>{t("exportCsv")}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.exportBtnJSON} onPress={exportToJSON}>
            <Feather name="download" size={13} color="#fff" />
            <Text style={styles.exportBtnText}>{t("exportJson")}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* ITEMS */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{t("orderItems")}</Text>

        {order?.details?.map((item: any) => {
          const appointment = item.appointments?.[0];
          const docUrl = dnAttachments[item._id];

          return (
            <View
              key={item._id}
              style={[styles.itemRow, isMobile && styles.itemRowMobile]}
            >
              <Image
                source={{ uri: item.imageUrl }}
                style={styles.productImage}
              />

              <View style={styles.itemContent}>
                <Text style={styles.itemCode}>{item.itemCode}</Text>
                <Text style={styles.itemName}>{item.itemName}</Text>
                <Text style={styles.meta}>
                  {t("qty")}: {item.quantity} {item.uom}
                </Text>
                <Text style={styles.meta}>
                  {t("warehouse")}: {item.warehouse?.name}
                </Text>
                <Text style={styles.meta}>
                  {t("requiredDate")}:{" "}
                  {new Date(item.requiredDate).toLocaleDateString()}
                </Text>
                <Text style={styles.meta}>
                  {t("perCarton")}: {item.perCtns} | {t("perPallet")}:{" "}
                  {item.perPallet}
                </Text>

                {item.documentNumber && (
                  <Text style={styles.meta}>
                    {t("documentNumber")}: {item.documentNumber}
                  </Text>
                )}

                {/* DN Attachment */}
                {item.documentNumber && (
                  <View style={styles.dnRow}>
                    {docUrl && !docUrl.includes("no-image.png") ? (
                      <TouchableOpacity
                        style={styles.dnBtn}
                        onPress={() => window.open(docUrl, "_blank")}
                      >
                        <Feather
                          name="file-text"
                          size={13}
                          color={isDark ? "#8ab4f8" : "#2563EB"}
                        />
                        <Text style={styles.dnBtnText}>
                          {t("viewDnAttachment")}
                        </Text>
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={[
                          styles.dnBtn,
                          { borderColor: colors.borderSubtle },
                        ]}
                        disabled={true}
                      >
                        <Feather
                          name="file-text"
                          size={13}
                          color={colors.textSecondary}
                        />
                        <Text
                          style={[
                            styles.dnBtnText,
                            { color: colors.textSecondary },
                          ]}
                        >
                          {t("viewDnAttachment")}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}

                {appointment && (
                  <Text style={styles.appointment}>
                    {t("appointment")}:{" "}
                    {new Date(appointment.startDateTime).toLocaleString()} –{" "}
                    {new Date(appointment.endDateTime).toLocaleTimeString()}
                  </Text>
                )}
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
};

const MetaRow = ({
  label,
  value,
  styles,
}: {
  label: string;
  value: string;
  styles: any;
}) => (
  <View style={styles.metaRow}>
    <Text style={styles.metaLabel}>{label}</Text>
    <Text style={styles.metaValue}>{value || "-"}</Text>
  </View>
);

const createStyles = (colors: ThemeColors, isDark: boolean) =>
  StyleSheet.create({
    page: { backgroundColor: colors.background, padding: 12 },
    header: { flexDirection: "row", alignItems: "center", marginBottom: 16 },
    headerRight: {
      flex: 1,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginLeft: 12,
    },
    orderNo: { fontSize: 18, fontWeight: "700", color: colors.text },
    statusChip: {
      backgroundColor: isDark ? "rgba(26, 115, 232, 0.2)" : "#e8f0fe",
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 12,
    },
    statusText: { fontWeight: "600", color: isDark ? "#8ab4f8" : "#1a73e8" },
    card: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: "700",
      marginBottom: 12,
      color: colors.text,
    },
    metaRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    metaLabel: { color: colors.textSecondary, fontWeight: "600" },
    metaValue: { fontWeight: "500", color: colors.text },

    // Export
    exportRow: {
      flexDirection: "row",
      gap: 10,
      marginBottom: 16,
    },
    exportBtnCSV: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      backgroundColor: "#16A34A",
      paddingVertical: 10,
      borderRadius: 8,
    },
    exportBtnJSON: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      backgroundColor: "#7C3AED",
      paddingVertical: 10,
      borderRadius: 8,
    },
    exportBtnText: { color: "#fff", fontSize: 13, fontWeight: "700" },

    // Items
    itemRow: {
      flexDirection: "row",
      marginBottom: 16,
      borderBottomWidth: 1,
      borderColor: colors.borderSubtle,
      paddingBottom: 16,
    },
    itemRowMobile: { flexDirection: "column" },
    productImage: {
      width: 90,
      height: 90,
      borderRadius: 8,
      marginRight: 12,
      backgroundColor: isDark ? colors.surfaceSubtle : "#f0f0f0",
    },
    itemContent: { flex: 1 },
    itemCode: { fontWeight: "700", fontSize: 14, color: colors.text },
    itemName: { fontSize: 13, marginBottom: 6, color: colors.text },
    meta: { fontSize: 12, color: colors.textSecondary },
    appointment: {
      marginTop: 6,
      fontSize: 12,
      fontWeight: "600",
      color: isDark ? "#8ab4f8" : "#1a73e8",
    },

    // DN attachment
    dnRow: { marginTop: 6 },
    dnBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      alignSelf: "flex-start",
      borderWidth: 1,
      borderColor: isDark ? "#8ab4f8" : "#2563EB",
      borderRadius: 6,
      paddingVertical: 4,
      paddingHorizontal: 10,
    },
    dnBtnText: {
      fontSize: 12,
      color: isDark ? "#8ab4f8" : "#2563EB",
      fontWeight: "600",
    },
    dnNone: { fontSize: 12, color: colors.textSecondary, marginTop: 4 },
  });

export default CustomerOrderSummaryReport;
